import { useEffect, useState } from "react";
import "../Stylesheets/Table/Table.css";
import { Funnel, Search, SortDownAlt, SortUpAlt, Download } from "react-bootstrap-icons";
import OutsideClickHandler from "react-outside-click-handler";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import api, { isCanceled } from "../lib/api";

const TableHeader = ({
    filter, exportHandler, exportLoad, setModals, setFilter, sortBy, addNewHandler,
    AddNewButtonIcon, exportButton, withoutAddNew, checkboxes, setNotification,
}) => {
    const [keyword, setKeyword] = useState(filter.keyword || "");
    const [admins, setAdmins] = useState([]);
    const [showFilterDropdown, setShowFilterDropdown] = useState(false);
    const userRole = useSelector((state) => state.user.role);

    // Derived from the router rather than window.location, so it stays correct
    // after a client-side navigation. The old checks tested for "bids-history"
    // while the actual route is "/bid-history", which meant the auction and
    // date filters never appeared on the Bids History page at all.
    const { pathname } = useLocation();
    const showAdminFilter = pathname.startsWith("/users");
    const showAuctionFilter = ["/accounts", "/watchlist", "/bid-history", "/won-vehicles", "/actions-history"]
        .some((route) => pathname.startsWith(route));
    const showDateFilter = ["/bid-history", "/won-vehicles", "/actions-history"]
        .some((route) => pathname.startsWith(route));
    const canAddNew = !withoutAddNew &&
        (userRole === "admin" || pathname.startsWith("/users") || pathname.startsWith("/accounts"));

    // Keeps the box in step when the filter is reset or restored from the URL.
    useEffect(() => {
        setKeyword(filter.keyword || "");
    }, [filter.keyword]);

    useEffect(() => {
        const controller = new AbortController();

        api.get('/admins', { signal: controller.signal })
            .then((res) => setAdmins(Array.isArray(res.data.data) ? res.data.data : []))
            .catch((err) => {
                // A non-admin gets a 403 here by design; only surface anything
                // else. Previously the whole handler was commented out, so a
                // real failure left the dropdown silently empty.
                if (isCanceled(err) || (err.response && err.response.status === 403)) return;
                setAdmins([]);
            });

        return () => controller.abort();
    }, []);

    const submitSearch = (e) => {
        e.preventDefault();
        setFilter({ keyword });
    };

    return (
        <div className="table-header-container">
            <div className="table-head-left-container">
                <form onSubmit={submitSearch} role="search">
                    <div className="form-group search-keyword-box" style={{ maxWidth: "300px" }}>
                        <input
                            type="search"
                            placeholder="Enter keyword"
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                            className="form-control"
                            name="keyword"
                            aria-label="Search"
                        />
                        <button className="search-button" type="submit" aria-label="Search"><Search /></button>
                    </div>
                </form>
                {/* aria-label on both: the <span> labels are hidden on narrow
                    screens to keep the toolbar on one row. */}
                {canAddNew && <button className="add-new-button" aria-label="Add new" onClick={() => {
                    // setModals is optional: pages that pass withoutAddNew have
                    // no modal to open.
                    if (setModals) setModals((prev) => ({ ...prev, createUser: true }));
                    if (addNewHandler) addNewHandler();
                }}><AddNewButtonIcon /><span>Add New</span></button>}
                {exportButton && <button
                    onClick={exportHandler}
                    disabled={exportLoad}
                    aria-label="Export"
                    className="export-button"
                ><Download />{exportLoad ? <div className="button-loader"></div> : <span>Export</span>}</button>}
            </div>
            <div className="table-head-right-container">
                <div className="filter-dropdown-container">
                    <button
                        type="button"
                        className="filter-dropdown-item"
                        aria-label="Filter"
                        aria-expanded={showFilterDropdown}
                        onClick={() => setShowFilterDropdown((prev) => !prev)}
                    >
                        <Funnel /> <span>Filter</span>
                    </button>
                    {showFilterDropdown && <OutsideClickHandler onOutsideClick={() => setShowFilterDropdown(false)}>
                        <div className="filter-dropdown-content">
                            {showAdminFilter && <div className="form-group">
                                <label htmlFor="filter-admin">Admin</label>
                                <select
                                    id="filter-admin"
                                    onChange={(e) => setFilter({ admin_id: e.target.value })}
                                    value={filter.admin_id ?? ""}
                                    className="form-control"
                                >
                                    <option value="">Select Admin</option>
                                    {admins.map((item) => <option value={item.id} key={item.id}>{item.name} {item.surname}</option>)}
                                </select>
                            </div>}
                            {showAuctionFilter && <div className="form-group">
                                <label htmlFor="filter-auction">Auction</label>
                                <select
                                    id="filter-auction"
                                    className="form-control"
                                    onChange={(e) => setFilter({ auction: e.target.value })}
                                    value={filter.auction ?? ""}
                                >
                                    <option value="">Select Auction</option>
                                    <option value="copart">Copart</option>
                                    <option value="iaai">IAAI</option>
                                </select>
                            </div>}
                            {showDateFilter && <div className="dates-filter">
                                <div className="form-group">
                                    <label htmlFor="filter-start">Start Date</label>
                                    <input
                                        id="filter-start"
                                        type="date"
                                        className="form-control"
                                        value={filter.start_date ?? ""}
                                        max={filter.end_date || undefined}
                                        onChange={(e) => setFilter({ start_date: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="filter-end">End Date</label>
                                    <input
                                        id="filter-end"
                                        type="date"
                                        className="form-control"
                                        value={filter.end_date ?? ""}
                                        min={filter.start_date || undefined}
                                        onChange={(e) => setFilter({ end_date: e.target.value })}
                                    />
                                </div>
                            </div>}
                            <div className="checkbox-filters">
                                {checkboxes && checkboxes.map((checkbox) => (
                                    <div className="form-group form-check-inline" key={checkbox.name}>
                                        {/* Controlled: without `checked` these boxes kept their
                                            DOM state after the filter was reset elsewhere. */}
                                        <input
                                            type="checkbox"
                                            id={checkbox.name}
                                            checked={filter[checkbox.name] === true || filter[checkbox.name] === "true"}
                                            onChange={(e) => setFilter({ [checkbox.name]: e.target.checked })}
                                        />
                                        <label htmlFor={checkbox.name}>{checkbox.value}</label>
                                    </div>
                                ))}
                            </div>
                            <button
                                type="button"
                                className="clear-filters-button"
                                onClick={() => {
                                    const cleared = { auction: "", admin_id: "", start_date: "", end_date: "" };
                                    (checkboxes || []).forEach((c) => { cleared[c.name] = false; });
                                    setFilter(cleared);
                                }}
                            >Clear filters</button>
                        </div>
                    </OutsideClickHandler>}
                </div>
                <div className="filter-inline">
                    <label htmlFor="sort-by" style={{ width: "65px" }}>Sort By:</label>
                    {typeof sortBy != "undefined" && <select
                        id="sort-by"
                        className="form-control"
                        // Bound to the filter: the box used to show the first
                        // option while the list was still sorted by something
                        // else entirely.
                        value={filter.sort_by ?? ""}
                        onChange={(e) => setFilter({ sort_by: e.target.value })}
                    >
                        {sortBy.length === 0 && <option value="">Sort By</option>}
                        {sortBy.map((item) => <option key={item.value} value={item.value}>{item.name}</option>)}
                    </select>}
                </div>
                <button
                    className="sort-order-button"
                    aria-label={filter.sort_order === "DESC" ? "Sort ascending" : "Sort descending"}
                    onClick={() => setFilter({ sort_order: filter.sort_order === "DESC" ? "ASC" : "DESC" })}
                > {filter.sort_order === "DESC" ? <SortDownAlt /> : <SortUpAlt />} </button>
            </div>
        </div>
    )
}

export default TableHeader
